import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Send, CheckCircle2, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { type DiagnosisResult } from '@/lib/index';
import { useDiagnosis } from '@/hooks/useDiagnosis';
import { springPresets } from '@/lib/motion';

const contactFormSchema = z.object({
  name: z.string().min(2, { message: '이름은 2글자 이상이어야 합니다.' }),
  email: z.string().email({ message: '올바른 이메일 주소를 입력해주세요.' }),
  subject: z.string().min(5, { message: '제목은 5글자 이상이어야 합니다.' }),
  message: z.string().min(10, { message: '내용은 10글자 이상이어야 합니다.' }),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

interface DiagnosisFormProps {
  onComplete: (result: DiagnosisResult) => void;
}

export function DiagnosisForm({ onComplete }: DiagnosisFormProps) {
  const {
    currentStep,
    totalSteps,
    currentQuestion,
    progress,
    isCompleted,
    result,
    answerQuestion,
    goToPreviousStep,
  } = useDiagnosis();

  useEffect(() => {
    if (isCompleted && result) {
      onComplete(result);
    }
  }, [isCompleted, result, onComplete]);

  if (isCompleted) return null;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8 space-y-4">
        <div className="flex items-center justify-between text-sm font-medium text-muted-foreground">
          <span className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4" />
            질문 {currentStep + 1} / {totalSteps}
          </span>
          <span className="font-mono">{progress}%</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={springPresets.gentle}
          className="space-y-8"
        >
          <div className="space-y-2">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              {currentQuestion.text}
            </h2>
            <p className="text-muted-foreground">
              가장 본인에게 해당한다고 생각되는 항목을 선택해주세요.
            </p>
          </div>

          <div className="grid gap-4">
            {currentQuestion.options.map((option, index) => (
              <Button
                key={index}
                variant="outline"
                className="h-auto p-6 text-left flex items-center justify-between group hover:border-primary hover:bg-primary/5 transition-all duration-300"
                onClick={() => answerQuestion(option.type)}
              >
                <span className="text-base md:text-lg font-medium">{option.text}</span>
                <CheckCircle2 className="w-5 h-5 opacity-0 group-hover:opacity-100 text-primary transition-opacity" />
              </Button>
            ))}
          </div>

          <div className="pt-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={goToPreviousStep}
              disabled={currentStep === 0}
              className="text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              이전 질문으로
            </Button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

interface ContactFormProps {
  onSubmit: (data: ContactFormValues) => void;
}

export function ContactForm({ onSubmit }: ContactFormProps) {
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: '',
      email: '',
      subject: '',
      message: '',
    },
  });

  const handleFormSubmit = (data: ContactFormValues) => {
    onSubmit(data);
    form.reset();
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>이름</FormLabel>
                  <FormControl>
                    <Input placeholder="홍길동" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>이메일</FormLabel>
                  <FormControl>
                    <Input placeholder="example@anabolife.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="subject"
            render={({ field }) => (
              <FormItem>
                <FormLabel>문의 제목</FormLabel>
                <FormControl>
                  <Input placeholder="문의하실 제목을 입력해주세요" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>상세 내용</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="궁금하신 점이나 체질 관리에 관한 문의 내용을 작성해주세요."
                    className="min-h-[150px] resize-none"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" className="w-full h-12 text-lg font-semibold group">
            <Send className="w-5 h-5 mr-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            문의하기 전송
          </Button>
        </form>
      </Form>
    </div>
  );
}
